import { ConcentrationRiskScore } from './ConcentrationRiskScore';
import { TrendBadge } from './TrendBadge';
import type { SiteConcentrationSummary, ConcentrationTrend } from '@/types';
import { formatQuarters, formatPercent, formatDate } from '@/utils/format';

interface ConcentrationSummaryCardProps {
  summary: SiteConcentrationSummary;
  isLoading?: boolean;
}

function SkeletonCard() {
  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
      <div className="flex gap-8">
        <div className="flex flex-col items-center gap-2 w-24">
          <div className="h-10 w-10 bg-mono-800 rounded animate-pulse" />
          <div className="h-2 w-16 bg-mono-800 rounded animate-pulse" />
          <div className="h-2 w-12 bg-mono-800 rounded animate-pulse" />
        </div>
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <div className="h-2 w-16 bg-mono-800 rounded animate-pulse mb-2" />
              <div className="h-4 w-20 bg-mono-800 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ConcentrationSummaryCard({
  summary,
  isLoading = false,
}: ConcentrationSummaryCardProps) {
  if (isLoading) return <SkeletonCard />;

  // Build closure range string: "~4.8 yr (1.5 – 7.8)"
  const closureMedian = formatQuarters(summary.closure_median_quarters);
  const hasRange = summary.closure_p10_quarters != null && summary.closure_p90_quarters != null;
  const closureP10 = formatQuarters(summary.closure_p10_quarters);
  const closureP90 = formatQuarters(summary.closure_p90_quarters);

  // Map site_trend to ConcentrationTrend for the TrendBadge.
  // site_trend can be null on sites with no analytical data (e.g., a freshly
  // created site before any predictions have been computed) — guard so the
  // page doesn't crash to a blank screen.
  const trendMap: Record<string, ConcentrationTrend> = {
    improving: 'decreasing',
    mixed: 'stable',
    worsening: 'increasing',
  };
  const siteTrend = summary.site_trend ?? null;
  const mappedTrend = (siteTrend && trendMap[siteTrend]) || 'stable';

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold text-mono-300 tracking-widest">
          CONCENTRATION RISK SUMMARY
        </h3>
        <span className="text-[10px] text-mono-600 tracking-wider">
          {formatDate(summary.prediction_date)}
        </span>
      </div>

      <div className="flex gap-8 items-start">
        {/* Left: Risk Score Gauge */}
        <div className="flex-shrink-0 w-24">
          <ConcentrationRiskScore
            score={summary.risk_score}
            category={summary.risk_category}
            size="md"
          />
        </div>

        {/* Right: Key Metrics Grid */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          {/* Site Trend */}
          <div>
            <div className="text-[10px] text-mono-600 tracking-widest mb-1">SITE TREND</div>
            <TrendBadge trend={mappedTrend} size="sm" />
            {siteTrend && (
              <span className="text-[9px] text-mono-600 ml-1 tracking-wider">
                ({siteTrend.toUpperCase()})
              </span>
            )}
          </div>

          {/* Wells Exceeding */}
          <div>
            <div className="text-[10px] text-mono-600 tracking-widest mb-1">WELLS EXCEEDING</div>
            <span className="text-sm font-bold text-mono-200">
              {summary.wells_exceeding_mcl_likely}
            </span>
            <span className="text-mono-500 text-xs"> / {summary.total_wells_assessed}</span>
          </div>

          {/* Closure Timeline with Monte Carlo spread */}
          <div>
            <div className="text-[10px] text-mono-600 tracking-widest mb-1">CLOSURE ESTIMATE</div>
            <span className="text-sm font-bold text-mono-200">
              {closureMedian}
            </span>
            {hasRange && (
              <div className="text-[10px] text-mono-500 mt-0.5">
                range: {closureP10} – {closureP90}
              </div>
            )}
          </div>

          {/* 10yr Probability */}
          <div>
            <div className="text-[10px] text-mono-600 tracking-widest mb-1">P(CLOSE IN 10YR)</div>
            <span className="text-sm font-bold text-mono-200">
              {formatPercent(summary.closure_probability_10yr)}
            </span>
          </div>

          {/* Improving Wells */}
          {summary.improving_wells_pct != null && (
            <div>
              <div className="text-[10px] text-mono-600 tracking-widest mb-1">IMPROVING WELLS</div>
              <span className="text-sm font-bold text-mono-200">
                {summary.improving_wells_pct.toFixed(0)}%
              </span>
            </div>
          )}

          {/* Bottleneck */}
          <div>
            <div className="text-[10px] text-mono-600 tracking-widest mb-1">BOTTLENECK</div>
            {summary.bottleneck_well_name ? (
              <div>
                <span className="text-sm text-mono-200">{summary.bottleneck_well_name}</span>
                {summary.bottleneck_analyte && (
                  <span className="text-mono-500 text-[10px] ml-1">({summary.bottleneck_analyte})</span>
                )}
              </div>
            ) : (
              <span className="text-mono-600">–</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
